import { useQuery, UseQueryResult, keepPreviousData } from '@tanstack/react-query'
import { createApiCall } from 'services'
import type { ITheme } from 'store/theme/types'

export interface EmailTemplateSummary {
  id: string
  name: string
  subject: string
  description: string
  group: 'app' | 'platform' | 'shared'
  category: string
  status: 'live' | 'draft'
  /**
   * The template's demo content — what the preview renders when nothing else is
   * supplied. VRG-560: the endpoint has always returned this (`listEmailTemplates`
   * emits it and the controller passes it straight through); the client type
   * simply never declared it, so the sample-data panel had no way to know WHICH
   * fields a template has without a hardcoded map.
   *
   * Read it, never assume it: a template with no `product` path gets no product
   * picker, rather than a control that writes nowhere.
   */
  sampleProps: Record<string, any>
}

/** Brand-customer email templates for the Studio Email tab (VERA-270). */
export const useGetEmailTemplates = (): UseQueryResult<EmailTemplateSummary[], Error> => {
  return useQuery({
    queryKey: ['emailTemplates'],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/companies/admin/me/email-templates',
      })
      return (response.data?.templates ?? []) as EmailTemplateSummary[]
    },
    staleTime: 1000 * 60 * 60,
  })
}

type EmailPreviewParams = {
  templateId?: string
  styles?: Partial<ITheme>
  logo?: string
  /**
   * VRG-560 — the sample-data panel's WHOLE props object, not a partial patch.
   *
   * The server's preview merge is a shallow top-level spread
   * (`{...sampleProps, ...props}`), so sending `{product: {title}}` would replace
   * the entire `product` object and blank its `sku` and `imgLink`. The panel
   * therefore seeds from `sampleProps` and writes picked values over it by path.
   *
   * Undefined until the panel is touched, which leaves the render byte-identical
   * to what it was before the panel existed.
   */
  props?: Record<string, any>
}

/**
 * Server-renders the selected template with the company's live (unsaved) theme +
 * logo applied. Keyed on the inputs so the iframe updates as the editor changes
 * (debounce the styles upstream to avoid a request per color tick).
 */
export const useEmailPreview = (params: EmailPreviewParams): UseQueryResult<string, Error> => {
  const { templateId, styles, logo, props } = params
  return useQuery({
    queryKey: ['emailPreview', templateId, styles, logo, props],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'POST',
        url: '/companies/admin/me/email-preview',
        data: { templateId, styles, logo, props },
      })
      return (response.data?.html ?? '') as string
    },
    enabled: !!templateId,
    // Keep the previous render on screen while the next one loads, so editing a
    // color/font doesn't flash the preview to a skeleton on every change.
    placeholderData: keepPreviousData,
  })
}
